import { getActiveParkingId } from "../../utils/tenant";
import React, { useEffect, useState, useMemo } from "react";
import DatePicker, { registerLocale } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import mn from "date-fns/locale/mn";
import { getFirestore, collection, query, where, getDocs, doc, onSnapshot } from "firebase/firestore";
import { 
    FiDownload, FiSearch, FiCalendar, FiFileText, FiPrinter, 
    FiUsers, FiClock, FiAlertTriangle, FiCheckCircle
} from "react-icons/fi";
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';

registerLocale("mn", mn);
const MySwal = withReactContent(Swal);

export default function TimeBalance() {
    const [selectedMonth, setSelectedMonth] = useState(new Date());
    const [balanceData, setBalanceData] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [holidays, setHolidays] = useState([]);
    
    // Системийн дүрмүүд (Далд байдлаар ажиллана)
    const [systemRules, setSystemRules] = useState({ gracePeriod: 15, overtimeThreshold: 60 });
    const [isStrictMode, setIsStrictMode] = useState(false);

    const db = getFirestore();

    const dateToString = (d) => {
        const pad = n => n.toString().padStart(2, '0');
        return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
    };

    const formatDuration = (totalMins) => {
        if (!totalMins || totalMins <= 0) return "0ц";
        const h = Math.floor(totalMins / 60);
        const m = totalMins % 60;
        return m > 0 ? `${h}ц ${m}м` : `${h}ц`;
    };

    // БАЯРЫН ӨДРҮҮД БОЛОН СИСТЕМИЙН ДҮРЭМ ТАТАХ
    useEffect(() => {
        const unsubHols = onSnapshot(collection(db, "parkings", getActiveParkingId(), "holidays"), snap => {
            setHolidays(snap.docs.map(d => d.data().date));
        });

        // Баазаас хатуу дүрэм (isStrictMode) тохиргоог далд уншина
        const unsubRules = onSnapshot(doc(db, "parkings", getActiveParkingId(), "settings", "general"), (d) => {
            if (d.exists()) {
                const data = d.data();
                setSystemRules(data);
                setIsStrictMode(data.isStrictMode === true);
            }
        });

        return () => { unsubHols(); unsubRules(); };
    }, [db]);

    // ЦАГИЙН БАЛАНС ТООЦООЛОХ
    useEffect(() => {
        const fetchBalance = async () => {
            setIsLoading(true);
            try {
                const year = selectedMonth.getFullYear();
                const month = selectedMonth.getMonth();
                const start = dateToString(new Date(year, month, 1));
                const end = dateToString(new Date(year, month + 1, 0));

                let workingDaysCount = 0;
                for (let d = new Date(year, month, 1); d <= new Date(year, month + 1, 0); d.setDate(d.getDate() + 1)) {
                    const dayOfWeek = d.getDay();
                    const dStr = dateToString(d);
                    if (dayOfWeek !== 0 && dayOfWeek !== 6 && !holidays.includes(dStr)) {
                        workingDaysCount++;
                    }
                }

                const empSnap = await getDocs(collection(db, "parkings", getActiveParkingId(), "employees"));
                const allEmps = empSnap.docs.map(d => ({ id: d.id, ...d.data() }));

                const attSnap = await getDocs(query(collection(db, "parkings", getActiveParkingId(), "daily_attendance"), 
                    where("date", ">=", start), where("date", "<=", end)));
                const allAtt = attSnap.docs.map(d => d.data());

                const calculated = allEmps.map(emp => {
                    const empLogs = allAtt.filter(l => l.emp_id === emp.emp_id);
                    
                    let totalActualMins = 0;
                    let totalApprovedOvertimeMins = 0; 
                    let actualDaysCount = 0;
                    let lates = 0, absents = 0, leaves = 0;
                    
                    const scheduleStr = emp.workSchedule || "08:00 - 17:00";
                    const [sTime, eTime] = scheduleStr.split("-").map(s => s.trim());
                    const [sH, sM] = sTime.split(":").map(Number);
                    const [eH, eM] = eTime.split(":").map(Number);
                    
                    let dailySchedMins = (eH * 60 + eM) - (sH * 60 + sM);
                    if (dailySchedMins < 0) dailySchedMins += 1440;
                    
                    const netDailySchedMins = dailySchedMins > 480 ? dailySchedMins - 60 : dailySchedMins;
                    const monthlyTargetMins = workingDaysCount * netDailySchedMins;

                    empLogs.forEach(log => {
                        if (log.status === 'late') { lates++; actualDaysCount++; }
                        if (log.status === 'ontime') actualDaysCount++;
                        if (log.status === 'absent') absents++;
                        if (['leave', 'annual_leave', 'business_trip', 'sick'].includes(log.status)) leaves++;

                        if (log.approvedOvertimeMins) {
                            totalApprovedOvertimeMins += Number(log.approvedOvertimeMins);
                        }

                        if (log.first_in && log.last_out) {
                            let inT = log.first_in.toDate ? log.first_in.toDate() : new Date(log.first_in);
                            let outT = log.last_out.toDate ? log.last_out.toDate() : new Date(log.last_out);
                            
                            // ⭐️ Дүрмийн дагуу чимээгүй тооцоолох
                            if (isStrictMode) {
                                const schedOutT = new Date(inT);
                                schedOutT.setHours(eH, eM, 0, 0);
                                if (outT > schedOutT) {
                                    outT = schedOutT; 
                                }
                            }

                            const workedMins = Math.floor((outT - inT) / 60000);
                            
                            let netWorkedMins = workedMins;
                            if (workedMins > 300) {
                                netWorkedMins = workedMins - 60;
                            }
                            
                            totalActualMins += Math.max(0, Math.min(netWorkedMins, netDailySchedMins));
                        }
                    });

                    return {
                        ...emp,
                        fullName: `${emp.lastName?.charAt(0) || ''}. ${emp.firstName || emp.name}`,
                        targetDays: workingDaysCount,
                        targetMins: monthlyTargetMins,
                        targetHrsText: formatDuration(monthlyTargetMins),
                        actualDays: actualDaysCount,
                        actualMins: totalActualMins,
                        actualHrsText: formatDuration(totalActualMins),
                        overtimeMins: totalApprovedOvertimeMins,
                        overtimeHrsText: formatDuration(totalApprovedOvertimeMins),
                        lates, absents, leaves
                    };
                });

                calculated.sort((a, b) => a.emp_id.localeCompare(b.emp_id));
                setBalanceData(calculated);
            } catch (error) { 
                console.error(error); 
            } finally { 
                setIsLoading(false); 
            }
        };
        fetchBalance();
    }, [db, selectedMonth, holidays, isStrictMode]); 

    const filtered = useMemo(() => {
        return balanceData.filter(d => 
            d.fullName.toLowerCase().includes(searchTerm.toLowerCase()) || 
            (d.emp_id && d.emp_id.toLowerCase().includes(searchTerm.toLowerCase()))
        );
    }, [balanceData, searchTerm]);

    const totals = useMemo(() => {
        return filtered.reduce((acc, row) => ({
            targetDays: acc.targetDays + (row.targetDays || 0),
            targetMins: acc.targetMins + (row.targetMins || 0),
            actualDays: acc.actualDays + (row.actualDays || 0),
            actualMins: acc.actualMins + (row.actualMins || 0),
            overtimeMins: acc.overtimeMins + (row.overtimeMins || 0),
            lates: acc.lates + (row.lates || 0),
            absents: acc.absents + (row.absents || 0),
            leaves: acc.leaves + (row.leaves || 0)
        }), { targetDays: 0, targetMins: 0, actualDays: 0, actualMins: 0, overtimeMins: 0, lates: 0, absents: 0, leaves: 0 });
    }, [filtered]);

    const exportToExcel = () => {
        const headers = ["Код", "Ажилтан", "Албан тушаал", "Ажиллавал зохих (Өдөр)", "Ажиллавал зохих (Цаг)", "Ажилласан (Өдөр)", "Ажилласан (Цаг)", "Илүү цаг", "Хоцролт", "Таслалт", "Чөлөө"];
        const rows = filtered.map(row => [
            row.emp_id, row.fullName, row.position || "-", 
            row.targetDays, row.targetHrsText, 
            row.actualDays, row.actualHrsText, 
            row.overtimeHrsText, row.lates, row.absents, row.leaves
        ]);
        const csvContent = "\uFEFF" + [headers.join(","), ...rows.map(e => e.join(","))].join("\n");
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `Цагийн_Баланс_${selectedMonth.getFullYear()}_${String(selectedMonth.getMonth()+1).padStart(2, '0')}.csv`;
        link.click();
    };

    const handlePrint = () => {
        const printWindow = window.open('', '', 'width=1000,height=800');
        const monthStr = `${selectedMonth.getFullYear()} оны ${selectedMonth.getMonth() + 1}-р сар`;
        
        // ⭐️ Хэвлэсэн огноо бэлдэх
        const today = new Date();
        const printDate = `${today.getFullYear()}.${String(today.getMonth() + 1).padStart(2, '0')}.${String(today.getDate()).padStart(2, '0')}`;
        
        let rowsHtml = '';
        filtered.forEach((row, i) => {
            rowsHtml += `
                <tr>
                    <td style="text-align: center;">${i + 1}</td>
                    <td>${row.emp_id}</td>
                    <td><b>${row.fullName}</b><br/><span style="font-size: 10px; color: #666;">${row.position || '-'}</span></td>
                    <td style="text-align: center;">${row.targetDays} өдөр</td>
                    <td style="text-align: right;">${row.targetHrsText}</td>
                    <td style="text-align: center;"><b>${row.actualDays} өдөр</b></td>
                    <td style="text-align: right;"><b>${row.actualHrsText}</b></td>
                    <td style="text-align: right;">${row.overtimeHrsText !== '0ц' ? row.overtimeHrsText : '-'}</td>
                    <td style="text-align: center;">${row.lates > 0 ? row.lates : '-'}</td>
                    <td style="text-align: center;">${row.absents > 0 ? row.absents : '-'}</td>
                    <td style="text-align: center;">${row.leaves > 0 ? row.leaves : '-'}</td>
                </tr>
            `;
        });

        const htmlContent = `
            <html>
            <head>
                <title>Цагийн баланс - ${monthStr}</title>
                <style>
                    body { font-family: 'Arial', sans-serif; font-size: 11px; color: #1e293b; padding: 30px; }
                    h2 { text-align: center; font-size: 16px; margin-bottom: 5px; text-transform: uppercase; color: #0f172a; }
                    p.subtitle { text-align: center; font-size: 12px; margin-bottom: 25px; color: #64748b; }
                    table { width: 100%; border-collapse: collapse; margin-bottom: 30px; }
                    th, td { border: 1px solid #cbd5e1; padding: 8px 10px; vertical-align: middle; }
                    th { background-color: #f8fafc; text-align: center; font-weight: bold; color: #334155; }
                    tfoot th { background-color: #f1f5f9; text-align: right; color: #0f172a; }
                    tfoot th.center { text-align: center; }
                </style>
            </head>
            <body>
                <h2>Байгууллагын цагийн балансын нэгтгэл</h2>
                <!-- ⭐️ Хэвлэсэн огноо нэмэгдсэн -->
                <p class="subtitle">Тайлант сар: ${monthStr} | Нийт ажилтан: ${filtered.length} | Огноо: ${printDate}</p>
                <table>
                    <thead>
                        <tr>
                            <th rowspan="2" style="width:30px;">№</th>
                            <th rowspan="2" style="width:60px;">Код</th>
                            <th rowspan="2">Ажилтан</th>
                            <th colspan="2">Ажиллавал зохих</th>
                            <th colspan="2">Ажилласан</th>
                            <th rowspan="2">Илүү цаг</th>
                            <th rowspan="2" style="width:50px;">Хоцролт</th>
                            <th rowspan="2" style="width:50px;">Таслалт</th>
                            <th rowspan="2" style="width:50px;">Чөлөө</th>
                        </tr>
                        <tr>
                            <th>Өдөр</th>
                            <th>Цаг</th>
                            <th>Өдөр</th>
                            <th>Цаг</th>
                        </tr>
                    </thead>
                    <tbody>
                        ${rowsHtml}
                    </tbody>
                    <tfoot>
                        <tr>
                            <th colspan="3">НИЙТ ДҮН:</th>
                            <th class="center">${totals.targetDays}</th>
                            <th>${formatDuration(totals.targetMins)}</th>
                            <th class="center">${totals.actualDays}</th>
                            <th>${formatDuration(totals.actualMins)}</th>
                            <th>${totals.overtimeMins > 0 ? formatDuration(totals.overtimeMins) : '-'}</th>
                            <th class="center">${totals.lates || '-'}</th>
                            <th class="center">${totals.absents || '-'}</th>
                            <th class="center">${totals.leaves || '-'}</th>
                        </tr>
                    </tfoot>
                </table>
                <div style="display: flex; justify-content: space-between; padding: 0 40px; margin-top: 50px; color: #475569; font-size: 12px;">
                    <div>Тайлан гаргасан: ........................................... / HR Менежер /</div>
                    <div>Хянасан: ........................................... / Нягтлан бодогч /</div>
                </div>
            </body>
            </html>
        `;
        printWindow.document.write(htmlContent);
        printWindow.document.close();
        printWindow.focus();
        setTimeout(() => { printWindow.print(); printWindow.close(); }, 500);
    };

    return (
        <div className="tw-bg-slate-50 tw-min-h-screen tw-p-4 sm:tw-p-6 lg:tw-p-8 tw-font-sans tw-text-slate-800">
            
            {/* ⭐️ HEADER */}
            <div className="tw-flex tw-flex-col md:tw-flex-row tw-justify-between tw-items-start md:tw-items-center tw-mb-8 tw-gap-4">
                <div>
                    <h2 className="tw-text-xl sm:tw-text-2xl lg:tw-text-3xl tw-font-black tw-text-slate-900 tw-tracking-tight tw-m-0 tw-flex tw-items-center tw-gap-3">
                        <div className="tw-p-2.5 tw-bg-indigo-600 tw-text-white tw-rounded-xl tw-shadow-sm"><FiFileText size={20} /></div>
                        Цагийн баланс
                    </h2>
                    <p className="tw-text-xs sm:tw-text-sm tw-text-slate-500 tw-mt-2 tw-mb-0 tw-ml-[56px]">Санхүү болон цалин бодолтод зориулсан сарын нэгтгэл тайлан</p>
                </div>
                
                <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-3">
                    <div className="tw-flex tw-items-center tw-bg-white tw-border tw-border-slate-200 tw-rounded-xl tw-shadow-sm tw-px-4 tw-py-2.5">
                        <FiCalendar className="tw-text-indigo-500 tw-mr-2" size={18} />
                        <DatePicker 
                            selected={selectedMonth} 
                            onChange={d => setSelectedMonth(d)} 
                            dateFormat="yyyy оны MM-р сар" 
                            showMonthYearPicker 
                            locale="mn" 
                            className="tw-outline-none tw-font-bold tw-text-sm tw-text-slate-700 tw-w-32 tw-bg-transparent tw-cursor-pointer" 
                        />
                    </div>
                    <button onClick={handlePrint} className="tw-p-2.5 tw-bg-white tw-border tw-border-slate-200 tw-text-slate-600 tw-rounded-xl hover:tw-bg-slate-100 tw-transition-colors tw-shadow-sm" title="Хэвлэх">
                        <FiPrinter size={18} />
                    </button>
                    <button onClick={exportToExcel} className="tw-p-2.5 tw-bg-slate-900 tw-text-white tw-rounded-xl hover:tw-bg-slate-800 tw-transition-colors tw-shadow-sm" title="CSV татах">
                        <FiDownload size={18} />
                    </button>
                </div>
            </div>

            {/* ⭐️ KPI CARDS */}
            <div className="tw-grid tw-grid-cols-2 md:tw-grid-cols-4 tw-gap-4 sm:tw-gap-6 tw-mb-8">
                <div className="tw-bg-white tw-p-5 tw-rounded-2xl tw-border tw-border-slate-200 tw-shadow-sm tw-flex tw-items-center tw-gap-4">
                    <div className="tw-w-10 tw-h-10 tw-bg-slate-50 tw-text-slate-500 tw-rounded-full tw-flex tw-items-center tw-justify-center"><FiUsers size={18}/></div>
                    <div>
                        <div className="tw-text-[10px] tw-font-bold tw-text-slate-400 tw-uppercase tw-tracking-widest">Нийт ажилтан</div>
                        <div className="tw-text-xl tw-font-black tw-text-slate-800">{filtered.length}</div>
                    </div>
                </div>
                <div className="tw-bg-white tw-p-5 tw-rounded-2xl tw-border tw-border-slate-200 tw-shadow-sm tw-flex tw-items-center tw-gap-4">
                    <div className="tw-w-10 tw-h-10 tw-bg-emerald-50 tw-text-emerald-500 tw-rounded-full tw-flex tw-items-center tw-justify-center"><FiCheckCircle size={18}/></div>
                    <div>
                        <div className="tw-text-[10px] tw-font-bold tw-text-slate-400 tw-uppercase tw-tracking-widest">Нийт ажилласан</div>
                        <div className="tw-text-xl tw-font-black tw-text-slate-800">{formatDuration(totals.actualMins)}</div>
                    </div>
                </div>
                <div className="tw-bg-white tw-p-5 tw-rounded-2xl tw-border tw-border-slate-200 tw-shadow-sm tw-flex tw-items-center tw-gap-4">
                    <div className="tw-w-10 tw-h-10 tw-bg-indigo-50 tw-text-indigo-500 tw-rounded-full tw-flex tw-items-center tw-justify-center"><FiClock size={18}/></div>
                    <div>
                        <div className="tw-text-[10px] tw-font-bold tw-text-slate-400 tw-uppercase tw-tracking-widest">Батлагдсан илүү цаг</div>
                        <div className="tw-text-xl tw-font-black tw-text-slate-800">{formatDuration(totals.overtimeMins)}</div>
                    </div>
                </div>
                <div className="tw-bg-white tw-p-5 tw-rounded-2xl tw-border tw-border-slate-200 tw-shadow-sm tw-flex tw-items-center tw-gap-4">
                    <div className="tw-w-10 tw-h-10 tw-bg-rose-50 tw-text-rose-500 tw-rounded-full tw-flex tw-items-center tw-justify-center"><FiAlertTriangle size={18}/></div>
                    <div>
                        <div className="tw-text-[10px] tw-font-bold tw-text-slate-400 tw-uppercase tw-tracking-widest">Зөрчил (Хоцролт, Таслалт)</div>
                        <div className="tw-text-xl tw-font-black tw-text-slate-800">{totals.lates + totals.absents} <span className="tw-text-sm tw-font-bold tw-text-slate-400">удаа</span></div>
                    </div>
                </div>
            </div>

            {/* ⭐️ MAIN DATA GRID */}
            <div className="tw-bg-white tw-border tw-border-slate-200 tw-rounded-2xl tw-shadow-sm tw-overflow-hidden tw-flex tw-flex-col">
                
                {/* Grid Toolbar */}
                <div className="tw-px-6 tw-py-4 tw-border-b tw-border-slate-200 tw-bg-white tw-flex tw-justify-between tw-items-center">
                    <h3 className="tw-text-sm tw-font-bold tw-text-slate-800 tw-m-0 tw-hidden sm:tw-block">Бүх ажилтны мэдээлэл</h3>
                    <div className="tw-relative tw-w-full sm:tw-w-72">
                        <FiSearch className="tw-absolute tw-left-3 tw-top-1/2 tw--translate-y-1/2 tw-text-slate-400" size={16} />
                        <input 
                            type="text" 
                            placeholder="Код эсвэл нэрээр хайх..." 
                            value={searchTerm} 
                            onChange={e => setSearchTerm(e.target.value)} 
                            className="tw-w-full tw-pl-9 tw-pr-4 tw-py-2.5 tw-bg-slate-50 tw-border tw-border-slate-200 tw-rounded-xl tw-text-sm tw-text-slate-800 focus:tw-outline-none focus:tw-border-indigo-500 tw-transition-colors" 
                        />
                    </div>
                </div>

                {/* Data Table */}
                <div className="tw-overflow-x-auto custom-scrollbar tw-flex-1">
                    <table className="tw-w-full tw-text-left tw-border-collapse tw-min-w-[1000px] tw-whitespace-nowrap">
                        <thead className="tw-sticky tw-top-0 tw-z-10 tw-bg-slate-50/95 tw-backdrop-blur-sm tw-border-b tw-border-slate-200">
                            <tr>
                                <th rowSpan="2" className="tw-px-4 tw-py-3 tw-text-[10px] tw-font-bold tw-text-slate-500 tw-uppercase tw-tracking-wider tw-text-center tw-w-12 tw-border-r tw-border-slate-100">№</th>
                                <th rowSpan="2" className="tw-px-4 tw-py-3 tw-text-[10px] tw-font-bold tw-text-slate-500 tw-uppercase tw-tracking-wider tw-border-r tw-border-slate-100">Ажилтан</th>
                                <th colSpan="2" className="tw-px-4 tw-py-2 tw-text-[10px] tw-font-bold tw-text-slate-500 tw-uppercase tw-tracking-wider tw-text-center tw-border-r tw-border-slate-100">Ажиллавал зохих</th>
                                <th colSpan="2" className="tw-px-4 tw-py-2 tw-text-[10px] tw-font-bold tw-text-slate-500 tw-uppercase tw-tracking-wider tw-text-center tw-border-r tw-border-slate-100">Ажилласан</th>
                                <th rowSpan="2" className="tw-px-4 tw-py-3 tw-text-[10px] tw-font-bold tw-text-slate-500 tw-uppercase tw-tracking-wider tw-text-right tw-w-24 tw-border-r tw-border-slate-100">Илүү цаг</th>
                                <th rowSpan="2" className="tw-px-4 tw-py-3 tw-text-[10px] tw-font-bold tw-text-slate-500 tw-uppercase tw-tracking-wider tw-text-center tw-w-20 tw-border-r tw-border-slate-100">Хоцролт</th>
                                <th rowSpan="2" className="tw-px-4 tw-py-3 tw-text-[10px] tw-font-bold tw-text-slate-500 tw-uppercase tw-tracking-wider tw-text-center tw-w-20 tw-border-r tw-border-slate-100">Таслалт</th>
                                <th rowSpan="2" className="tw-px-4 tw-py-3 tw-text-[10px] tw-font-bold tw-text-slate-500 tw-uppercase tw-tracking-wider tw-text-center tw-w-20">Чөлөө</th>
                            </tr>
                            <tr>
                                <th className="tw-bg-slate-50/50 tw-border-b tw-border-r tw-border-slate-200 tw-px-4 tw-py-2 tw-text-[10px] tw-font-bold tw-text-slate-400 tw-uppercase tw-tracking-wider tw-text-center">Өдөр</th>
                                <th className="tw-bg-slate-50/50 tw-border-b tw-border-r tw-border-slate-200 tw-px-4 tw-py-2 tw-text-[10px] tw-font-bold tw-text-slate-400 tw-uppercase tw-tracking-wider tw-text-right">Цаг</th>
                                <th className="tw-bg-slate-50/50 tw-border-b tw-border-r tw-border-slate-200 tw-px-4 tw-py-2 tw-text-[10px] tw-font-bold tw-text-slate-400 tw-uppercase tw-tracking-wider tw-text-center">Өдөр</th>
                                <th className="tw-bg-slate-50/50 tw-border-b tw-border-r tw-border-slate-200 tw-px-4 tw-py-2 tw-text-[10px] tw-font-bold tw-text-slate-400 tw-uppercase tw-tracking-wider tw-text-right">Цаг</th>
                            </tr>
                        </thead>
                        <tbody className="tw-divide-y tw-divide-slate-100">
                            {isLoading ? (
                                <tr><td colSpan={10} className="tw-py-20 tw-text-center tw-text-slate-400 tw-text-sm">Мэдээлэл боловсруулж байна...</td></tr> 
                            ) : filtered.length === 0 ? (
                                <tr><td colSpan={10} className="tw-py-20 tw-text-center tw-text-slate-400 tw-text-sm">Мэдээлэл олдсонгүй</td></tr> 
                            ) : (
                                filtered.map((row, idx) => (
                                    <tr key={row.emp_id} className="hover:tw-bg-slate-50/80 tw-transition-colors">
                                        <td className="tw-px-4 tw-py-3 tw-text-center tw-text-xs tw-text-slate-400 tw-font-medium tw-border-r tw-border-slate-100">{idx + 1}</td>
                                        <td className="tw-px-4 tw-py-3 tw-border-r tw-border-slate-100">
                                            <div className="tw-flex tw-items-center tw-gap-3">
                                                <div className="tw-w-8 tw-h-8 tw-rounded-full tw-bg-gradient-to-br tw-from-slate-100 tw-to-slate-200 tw-text-slate-600 tw-flex tw-items-center tw-justify-center tw-font-bold tw-text-xs tw-flex-shrink-0">
                                                    {(row.firstName || row.name || "A").charAt(0)}
                                                </div>
                                                <div>
                                                    <div className="tw-text-sm tw-font-bold tw-text-slate-800">{row.fullName}</div>
                                                    <div className="tw-text-[10px] tw-text-slate-500 tw-font-medium tw-mt-0.5">{row.emp_id} • {row.position || "Ажилтан"}</div>
                                                </div>
                                            </div>
                                        </td>
                                        
                                        {/* Ажиллавал зохих */}
                                        <td className="tw-px-4 tw-py-3 tw-text-center tw-text-sm tw-text-slate-500 tw-border-r tw-border-slate-100">{row.targetDays}</td>
                                        <td className="tw-px-4 tw-py-3 tw-text-right tw-text-sm tw-text-slate-500 tw-border-r tw-border-slate-100">{row.targetHrsText}</td>
                                        
                                        {/* Ажилласан */}
                                        <td className="tw-px-4 tw-py-3 tw-text-center tw-text-sm tw-font-bold tw-text-slate-700 tw-border-r tw-border-slate-100">{row.actualDays}</td>
                                        <td className="tw-px-4 tw-py-3 tw-text-right tw-text-sm tw-font-black tw-text-slate-900 tw-border-r tw-border-slate-100">{row.actualHrsText}</td>
                                        
                                        {/* Илүү цаг */}
                                        <td className="tw-px-4 tw-py-3 tw-text-right tw-border-r tw-border-slate-100">
                                            <span className={`tw-text-xs tw-font-bold ${row.overtimeHrsText !== "0ц" ? 'tw-text-slate-800' : 'tw-text-slate-300'}`}>
                                                {row.overtimeHrsText !== "0ц" ? row.overtimeHrsText : "-"}
                                            </span>
                                        </td>
                                        
                                        {/* Зөрчил / Чөлөө */}
                                        <td className="tw-px-4 tw-py-3 tw-text-center tw-border-r tw-border-slate-100">
                                            {row.lates > 0 ? (
                                                <span className="tw-inline-block tw-px-2 tw-py-0.5 tw-bg-amber-50 tw-text-amber-700 tw-rounded tw-text-xs tw-font-bold">{row.lates}</span>
                                            ) : <span className="tw-text-slate-300 tw-font-bold">-</span>}
                                        </td>
                                        <td className="tw-px-4 tw-py-3 tw-text-center tw-border-r tw-border-slate-100">
                                            {row.absents > 0 ? (
                                                <span className="tw-inline-block tw-px-2 tw-py-0.5 tw-bg-rose-50 tw-text-rose-700 tw-rounded tw-text-xs tw-font-bold">{row.absents}</span>
                                            ) : <span className="tw-text-slate-300 tw-font-bold">-</span>}
                                        </td>
                                        <td className="tw-px-4 tw-py-3 tw-text-center">
                                            {row.leaves > 0 ? (
                                                <span className="tw-inline-block tw-px-2 tw-py-0.5 tw-bg-slate-100 tw-text-slate-700 tw-rounded tw-text-xs tw-font-bold">{row.leaves}</span>
                                            ) : <span className="tw-text-slate-300 tw-font-bold">-</span>}
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                        
                        {/* ⭐️ НИЙТ ДҮН (FOOTER) */}
                        {!isLoading && filtered.length > 0 && (
                            <tfoot className="tw-bg-slate-50 tw-border-t-2 tw-border-slate-200">
                                <tr>
                                    <td colSpan="2" className="tw-px-4 tw-py-4 tw-text-right tw-text-[11px] tw-font-black tw-text-slate-500 tw-uppercase tw-tracking-widest tw-border-r tw-border-slate-200">Нийт дүн:</td>
                                    <td className="tw-px-4 tw-py-4 tw-text-center tw-text-sm tw-font-bold tw-text-slate-700 tw-border-r tw-border-slate-200">{totals.targetDays}</td>
                                    <td className="tw-px-4 tw-py-4 tw-text-right tw-text-sm tw-font-bold tw-text-slate-700 tw-border-r tw-border-slate-200">{formatDuration(totals.targetMins)}</td>
                                    <td className="tw-px-4 tw-py-4 tw-text-center tw-text-sm tw-font-black tw-text-slate-900 tw-border-r tw-border-slate-200">{totals.actualDays}</td>
                                    <td className="tw-px-4 tw-py-4 tw-text-right tw-text-sm tw-font-black tw-text-slate-900 tw-border-r tw-border-slate-200">{formatDuration(totals.actualMins)}</td>
                                    <td className="tw-px-4 tw-py-4 tw-text-right tw-text-sm tw-font-bold tw-text-slate-800 tw-border-r tw-border-slate-200">{totals.overtimeMins > 0 ? formatDuration(totals.overtimeMins) : "-"}</td>
                                    <td className="tw-px-4 tw-py-4 tw-text-center tw-text-sm tw-font-bold tw-text-amber-600 tw-border-r tw-border-slate-200">{totals.lates || "-"}</td>
                                    <td className="tw-px-4 tw-py-4 tw-text-center tw-text-sm tw-font-bold tw-text-rose-500 tw-border-r tw-border-slate-200">{totals.absents || "-"}</td>
                                    <td className="tw-px-4 tw-py-4 tw-text-center tw-text-sm tw-font-bold tw-text-slate-700">{totals.leaves || "-"}</td>
                                </tr>
                            </tfoot>
                        )}
                    </table>
                </div>
            </div>

            <style jsx="true">{`
                .custom-scrollbar::-webkit-scrollbar { height: 8px; width: 8px; }
                .custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
                .custom-scrollbar::-webkit-scrollbar-thumb { background-color: #cbd5e1; border-radius: 20px; }
            `}</style>
        </div>
    );
}